/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { required } from '@src/app/shared/validators';
import { CommsTemplateService } from '@src/app/modules/social/comms-template-page/comms-templates.service';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormPickLinkComponent } from '@src/app/shared/form/form-pick-link/form-pick-link.component';
import { CommsTemplate, CommsTemplateType } from '@src/app/model/CommsTemplate';
import { of } from 'rxjs';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { Field } from '@src/app/shared/field/Field';
import { Person } from '@src/app/model/person';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Txn } from '@src/app/model/txn';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { RequestService } from '@src/app/modules/crm/request.service';
import { InvoiceService } from '@src/app/modules/txn/invoice.service';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { Omc } from '@src/app/model/Omc';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { InvoicePageNavRoute } from '@src/app/routes/invoice-page-nav';
import { CommsTemplatePageNavRoute } from '@src/app/routes/comms-template-page-nav';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MessageService } from '@src/app/shared/message.service';
import { MatDialog } from '@angular/material/dialog';
import { PreviewMailDialogComponent } from '@src/app/modules/social/comms-template-page/preview-mail-dialog.component';

@Component({
    selector: 'app-comms-template-page',
    imports: [FormPageComponent],
    templateUrl: './comms-template-page.component.html',
    styleUrl: './comms-template-page.component.scss'
})
export class CommsTemplatePageComponent extends AbstractPageComponent {

    dataSvc = inject(CommsTemplateService);
    agentDataSvc = inject(AgentDataService);
    currentUser = inject(CurrentUserService).currentUser;
    msgSvc = inject(MessageService);
    dialog = inject(MatDialog);

    titleField = FormTextComponent.make('Title', 'title', { validators: [required], cellOpts: { width: '25%' }, formColumn: 1 });

    editor = FormRichTextComponent.make('Content', 'content', { formColumn: 2, validators: [required], availReplacements: [], richTextHeight: '650' });

    typePickList = FormPicklistComponent.make('Type', 'type', null, {
        items: CommsTemplateType.TYPES, refreshes: [(t: CommsTemplateType) => this.onTypeChange(t)]
    }, { validators: [required], formColumn: 1 });

    templateDescriptionField = FormTextAreaComponent.make('Description', 'templateDescription', {
        readonly: true, disable: true, sendServer: false, formColumn: 1, visible: Field.formOnly,
        // fieldSet.setValue() runs right after beforeEdit() with emitEvent:false, so the
        // manual control.setValue() in onTypeChange() below gets overwritten on initial load
        // unless the value can also be derived here from the item itself.
        calculateValue: (c: CommsTemplate) => CommsTemplateType.TYPES.find(ct => ct.id === c.type)?.description ?? ''
    });

    parentField = FormPicklistComponent.make('Parent', 'parentId', 'parentId',
        { service: this.dataSvc, optionDisplayValue: (o) => o.title },
        { hint: $localize`Allows for example questions templates, to be linked to a ballot template`, formColumn: 1 }
    );

    omcField = FormPicklistComponent.make('OMC', 'omcId', null,
        { items: [], optionValue: (o: Omc) => o?.omcTeamId },
        { formColumn: 3, visible: Field.noShow, sendServer: false }
    );

    constructor() {
        super();
        this.agentDataSvc.getOmcList().subscribe(omcs => this.omcField.setPicklistItems(omcs));
    }

    personSampleField = FormPickLinkComponent.make({
        label: 'Sample Person',
        value: 'personSampleId',
        textValue: () => '',
        nav: PersonPageNavRoute,
        pick: {
            service: inject(PersonService),
            config: () => Person.pickDialogFormConfig(),
            forceTeamId: () => this.omcField.control.value,
            textFrom: (p: Person) => p.fullName,
            allowNull: true,
        },
    }, { formColumn: 3, visible: Field.noShow, sendServer: false });

    requestSampleField = FormPickLinkComponent.make({
        label: 'Sample Request',
        value: 'requestSampleId',
        textValue: () => '',
        nav: ServiceRequestPageNavRoute,
        pick: {
            service: inject(RequestService),
            config: () => ServiceRequest.pickDialogFormConfig(),
            forceTeamId: () => this.omcField.control.value,
            textFrom: (sr: ServiceRequest) => sr.title,
            allowNull: true,
        },
    }, { formColumn: 3, visible: Field.noShow, sendServer: false });

    invoiceSampleField = FormPickLinkComponent.make({
        label: 'Sample Invoice',
        value: 'invoiceSampleId',
        textValue: () => '',
        nav: InvoicePageNavRoute,
        pick: {
            service: inject(InvoiceService),
            config: () => Txn.pickDialogFormConfig(),
            // Only charges that came from a real billing run - guarantees getInvoicePDF() always
            // finds an already-generated PDF, so Test/Preview never generates one on the fly.
            filter: () => ({ billingId: 'NOT NULL' }),
            forceTeamId: () => this.omcField.control.value,
            textFrom: (t: Txn) => `#${t.refNr}`,
            allowNull: true,
        },
    }, { formColumn: 3, visible: Field.noShow, sendServer: false });

    previewButton = FormButtonComponent.makeIconButton('Preview', 'preview', 'Preview the merged content against the chosen sample record', () => this.doPreview(), 'Preview')
        .override({ formColumn: 3, visible: Field.noShow });

    testButton = FormButtonComponent.makeIconButton('Test', 'science', 'Send a test email to yourself using the chosen sample record', () => this.doTest(), 'Test')
        .override({ formColumn: 3, visible: Field.noShow });

    config = new FormConfig({
        navRoute: CommsTemplatePageNavRoute,
        docPath: 'omc-professional/communication-templates',
        title: $localize`Communication Templates`,
        help: $localize`Templates for communications`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.titleField,
                this.typePickList,
                this.templateDescriptionField,
                this.parentField,
                this.editor,
                this.omcField,
                this.personSampleField,
                this.requestSampleField,
                this.invoiceSampleField,
                this.previewButton,
                this.testButton,
            ],
            formLayout: [{ cells: [{ width: '20%' }, { width: '60%' }, { width: '20%' }] }]
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: () => of(new CommsTemplate()),
        beforeEdit: this.beforeEdit.bind(this)
    });

    beforeEdit(c: CommsTemplate) {
        const type = CommsTemplateType.TYPES.find(ct => ct.id === c.type);
        this.onTypeChange(type);
        return c;
    }

    private onTypeChange(t: CommsTemplateType) {
        this.editor.availReplacements = t?.replacements?.length > 0 ? t.replacements.slice() : [];
        this.templateDescriptionField.control.setValue(t?.description ?? '');

        const testable = !!t?.sampleKind;
        this.omcField.visible = testable ? Field.formOnly : Field.noShow;
        this.previewButton.visible = testable ? Field.formOnly : Field.noShow;
        this.testButton.visible = testable ? Field.formOnly : Field.noShow;
        this.personSampleField.visible = t?.sampleKind === 'person' ? Field.formOnly : Field.noShow;
        this.requestSampleField.visible = t?.sampleKind === 'request' ? Field.formOnly : Field.noShow;
        this.invoiceSampleField.visible = t?.sampleKind === 'invoice' ? Field.formOnly : Field.noShow;
    }

    private activeSampleId(): string {
        switch (this.selectedType()?.sampleKind) {
            case 'person': return this.personSampleField.control.value;
            case 'request': return this.requestSampleField.control.value;
            case 'invoice': return this.invoiceSampleField.control.value;
            default: return null;
        }
    }

    private selectedType(): CommsTemplateType {
        return CommsTemplateType.TYPES.find(t => t.id === this.typePickList.control.value);
    }

    private doPreview() {
        const sampleId = this.activeSampleId();
        if (!sampleId) {
            this.msgSvc.show($localize`Pick a sample record first`);
            return;
        }
        this.dataSvc.preview(this.typePickList.control.value, this.editor.control.value, sampleId, this.titleField.control.value, this.omcField.control.value)
            .subscribe(result => {
                if (result) {
                    this.dialog.open(PreviewMailDialogComponent, { data: { title: $localize`Preview`, html: result.html }, width: '80vw', maxWidth: '80vw' });
                }
            });
    }

    private doTest() {
        const sampleId = this.activeSampleId();
        if (!sampleId) {
            this.msgSvc.show($localize`Pick a sample record first`);
            return;
        }
        this.dataSvc.sendTest(this.typePickList.control.value, this.editor.control.value, sampleId, this.titleField.control.value, this.omcField.control.value)
            .subscribe(result => {
                if (result) {
                    this.msgSvc.show($localize`Test sent to ${this.currentUser?.email} — check your inbox.`);
                }
            });
    }

}
